import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, ShieldCheck, HeartPulse, Sparkles, Beaker, CheckCircle2 } from "lucide-react";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import ContactSection from "@/components/ContactSection";
import SharedFaq from "@/components/SharedFaq";
import { myths, articles } from "@/data/articles";

const heroImg = "https://images.unsplash.com/photo-1687672507171-8e5f01f3d16e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxoZWFsdGh5JTIwaGFwcHklMjBwZXJzb24lMjBlYXRpbmd8ZW58MHx8fHwxNzg3Mjk5Nzc1fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div className="grain-bg">
      <Seo
        title="Mit Lactoseintoleranz normal leben"
        description="Lactoseintoleranz ist keine Krankheit. Erfahre wie du Milchprodukte wieder genießt und deine Toleranz in 12 Wochen wissenschaftlich fundiert wiederherstellst."
        keywords="Lactoseintoleranz, Milchzucker, Ernährungsplan, Toleranzaufbau, Mikrobiom, LacLive"
        path="/"
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium mb-6" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> Wissenschaftlich fundiert · Klar erklärt
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-900 leading-[1.05]">
              Mit Lactoseintoleranz <span className="text-emerald-600">völlig normal</span> leben.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Lactoseintoleranz ist keine Krankheit - und lactosefreie Produkte sind meist unnötig. LacLive zeigt evidenzbasiert, wie du Milchprodukte wieder genießt und deine Toleranz gezielt wiederherstellst.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/paywall" data-testid="hero-cta-primary">
                <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 h-12 btn-lift">
                  Ernährungsplan freischalten <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/ueber-lactoseintoleranz" data-testid="hero-cta-secondary">
                <Button variant="outline" className="rounded-full h-12 px-6 btn-lift">
                  Mehr erfahren
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "75 %", l: "Weltweite Verbreitung" },
                { n: "12 g", l: "meist verträglich / Mahlzeit" },
                { n: "12 Wo.", l: "Toleranz-Aufbau möglich" }
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-2xl font-bold text-slate-900">{s.n}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <ImagePlaceholder src={heroImg} label="Hero Bild" ratio="aspect-[4/5]" className="shadow-[0_30px_80px_-20px_rgba(15,23,42,0.25)]" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-slate-100">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <HeartPulse className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Studienbasiert</div>
                  <div className="text-sm font-semibold text-slate-900">Ernährungsmedizin 2024</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-slate-200 bg-white/60">
        <div className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-4 gap-8 text-center md:text-left">
          {[
            { icon: ShieldCheck, t: "Evidenzbasiert", d: "Nur belegte Fakten." },
            { icon: Beaker, t: "Ernährungsmedizin", d: "Aktuelle Studienlage." },
            { icon: HeartPulse, t: "Alltagstauglich", d: "Konkrete Schritte." },
            { icon: CheckCircle2, t: "Ohne Verzicht", d: "Vollwertig genießen." },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{f.t}</div>
                  <div className="text-sm text-slate-500">{f.d}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Myths bento */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Mythen & Fakten</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Was stimmt wirklich über Lactoseintoleranz?</h2>
          <p className="mt-4 text-slate-600 text-lg">Viele Annahmen halten sich hartnäckig. Hier sind fünf davon - klar widerlegt.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {myths.slice(0, 5).map((m, i) => (
            <Card key={i} className="p-7 rounded-2xl border-slate-100 card-lift" data-testid={`myth-card-${i}`}>
              <div className="text-xs uppercase tracking-wide text-red-500 font-semibold mb-3">Mythos</div>
              <div className="text-slate-900 font-semibold text-lg leading-snug mb-4">{m.myth}</div>
              <div className="border-t border-slate-100 pt-4">
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-2">Fakt</div>
                <div className="text-slate-600 leading-relaxed">{m.fact}</div>
              </div>
            </Card>
          ))}
          <Card className="p-7 rounded-2xl bg-slate-900 text-white card-lift flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-emerald-300 font-semibold mb-3">Alle Fakten</div>
              <div className="font-semibold text-xl leading-snug">Umfassender Überblick - alle gängigen Mythen im Detail widerlegt.</div>
            </div>
            <Link to="/mythen-und-fakten" className="mt-6" data-testid="myths-cta">
              <Button className="rounded-full bg-white text-slate-900 hover:bg-slate-100 btn-lift">
                Zur Übersicht <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>

      {/* Program teaser */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-slate-100 p-8 lg:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Der 12-Wochen-Ernährungsplan</div>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Toleranz Schritt für Schritt zurückgewinnen</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">
              Ein strukturierter Plan mit klaren Mengenangaben, alltagstauglichen Rezepten und wöchentlichen Steigerungen - damit dein Körper Lactose wieder verträgt.
            </p>
            <ul className="mt-6 space-y-3">
              {["16 alltagstaugliche Rezepte", "12 Wochen in 5 klar strukturierten Phasen", "Einmalzahlung - dauerhafter Zugang"].map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-700"><CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Link to="/paywall" className="inline-block mt-8" data-testid="program-cta">
              <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 btn-lift">
                Jetzt für 19,99 € freischalten <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <ImagePlaceholder src="https://images.pexels.com/photos/4929677/pexels-photo-4929677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" label="Meal Prep" ratio="aspect-[4/3]" />
        </div>
      </section>

      {/* Articles */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Aktuelle Artikel</div>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Klar, fundiert, hilfreich</h2>
          </div>
          <Link to="/artikel" className="hidden md:inline-flex text-sm text-emerald-700 font-medium hover:text-emerald-800" data-testid="articles-cta">Alle Artikel →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {articles.map((a) => (
            <Link key={a.slug} to={`/artikel/${a.slug}`} className="group" data-testid={`article-card-${a.slug}`}>
              <ImagePlaceholder src={a.image} label="Artikelbild" ratio="aspect-[16/10]" className="mb-5" />
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">{a.date} · {a.read}</div>
              <h3 className="font-semibold text-slate-900 text-lg leading-snug group-hover:text-emerald-700 transition-colors">{a.title}</h3>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">{a.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>

      <SharedFaq />

      <ContactSection />
    </div>
  );
}
